/**
 * Pay-per-booking commission billing (the Free plan's "add a card" step).
 *
 * Mirrors paypalApiCredentialsService.ts's auth pattern: a Firebase ID token
 * proves the caller owns this businessId, verified server-side in
 * backend/server.js's verifyBusinessOwnerRequest. See that file's
 * /api/billing/setup-intent(+/confirm) routes.
 */

import { Platform } from 'react-native';
import { WIDGET_BOOKING_API } from '../utils/widgetEmbed';

const API_BASE_URL = __DEV__ ? 'http://localhost:3000/api' : WIDGET_BOOKING_API;

async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    if (Platform.OS === 'web') {
      const { getWebAuth } = require('./unifiedAuthService');
      const auth = getWebAuth();
      const token = await auth?.currentUser?.getIdToken?.();
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const { getApp } = require('@react-native-firebase/app');
    const { getAuth } = require('@react-native-firebase/auth');
    const token = await getAuth(getApp())?.currentUser?.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export interface BillingSetupIntent {
  success: boolean;
  clientSecret?: string;
  setupIntentId?: string;
  error?: string;
}

/** Starts card-on-file setup for the Free (pay-per-booking) plan. */
export async function createBillingSetupIntent(
  businessId: string,
  businessEmail: string,
  businessName: string
): Promise<BillingSetupIntent> {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/billing/setup-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ businessId, businessEmail, businessName }),
    });
    const result = await response.json();
    return result;
  } catch (error: any) {
    return { success: false, error: error?.message || 'Could not start card setup.' };
  }
}

/**
 * Confirms the SetupIntent server-side (which re-verifies with Stripe
 * before activating anything) and activates the pay-per-booking plan.
 */
export async function confirmBillingSetupIntent(
  businessId: string,
  setupIntentId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/billing/setup-intent/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ businessId, setupIntentId }),
    });
    return await response.json();
  } catch (error: any) {
    return { success: false, error: error?.message || 'Could not confirm card setup.' };
  }
}
